import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bird, Shield, Phone, Check, MapPin, ChevronRight, Award, Calculator, RotateCcw, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Header, Logo } from "../components/Layout";
import GutterCleanGallery from "../components/GutterCleanGallery";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// SEO Component
const PageSEO = () => (
  <>
    <title>Solar Bird Proofing Ripley | Pigeon Mesh Install | Pest Free</title>
    <meta name="description" content="Professional solar panel bird proofing in Ripley, QLD. Pigeon nest removal, premium mesh with 10-year warranty. Serving Ripley, Providence & Springfield." />
    <meta name="keywords" content="solar bird proofing Ripley, pigeon control Ripley QLD, solar panel mesh Ripley, bird proofing Providence, pigeon removal Ecco Ripley, solar panel protection Springfield" />
    <link rel="canonical" href="https://pestfreebrisbane.com.au/solar-bird-proofing-ripley" />
    <meta property="og:title" content="Solar Panel Bird Proofing Ripley | Pest Free Ipswich & Brisbane" />
    <meta property="og:description" content="Professional solar panel bird proofing with 10-year warranty. Remove pigeons and protect your panels. Call (07) 3708 1422." />
    <meta property="og:url" content="https://pestfreebrisbane.com.au/solar-bird-proofing-ripley" />
  </>
);

// Theme colors for birds - Blue
const theme = {
  primary: "#5BC0DE",
  primaryDark: "#4AA8C4",
  secondary: "#7ABC32",
  gradient: "from-[#5BC0DE] to-[#3498DB]"
};

export default function SolarBirdProofingRipleyPage() {
  const calculatorRef = useRef(null);
  
  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const services = [
    { 
      title: "Solar Panel Bird Mesh", 
      desc: "Premium mesh system installed around your solar panels to prevent bird access. 10-year warranty on mesh." 
    },
    { 
      title: "Nest Removal & Cleanup", 
      desc: "Complete removal of all nests, debris, and droppings from under your panels." 
    },
    { 
      title: "Bird Netting", 
      desc: "Custom netting solutions for carports, pergolas, and other structures." 
    },
    { 
      title: "Bird Spiking", 
      desc: "Humane deterrent systems for ledges, gutters, and roof lines." 
    }
  ];

  const whatsIncluded = [
    "Removal of all nests & debris",
    "Disposal of all nests",
    "Blow sticks and loose debris from under panels and off roof with petrol blower",
    "Professional-grade mesh installation",
    "10-year warranty on mesh",
    "Complete before & after photos and videos"
  ];

  const premiumCleaningIncludes = [
    "Full wash all around and under panels",
    "Clean water-fed broom finish on panel tops",
    "Dispose of 2-5 bin bags of poo, sticks & nests from gutters & under panels",
    "Full gutter clear"
  ];

  const ripleyAreas = [
    "Ripley Valley",
    "Providence",
    "Ecco Ripley", 
    "South Ripley",
    "Deebing Heights",
    "Springfield",
    "Redbank Plains"
  ];

  return (
    <div className="min-h-screen">
      <PageSEO />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="ripley-birds-hero">
        <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient}`} />
        <div className="absolute inset-0 bg-slate-900/20" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <MapPin className="w-5 h-5 text-white" />
              <span className="font-medium text-white">Servicing Ripley & Surrounds</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight text-white">
              Solar Panel Bird Proofing <span className="text-yellow-300">Ripley</span>
            </h1>
            
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Premium pigeon control for Ripley homes. We remove nests and install a professional-grade mesh system backed by a 10-year warranty.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={scrollToCalculator}
                className="bg-white text-[#5BC0DE] hover:bg-slate-100 text-lg px-8 py-4 rounded-full font-semibold shadow-lg flex items-center justify-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Get Instant Estimate
              </Button>
              <a 
                href="tel:0737081422"
                className="inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white border-2 border-white text-lg px-8 py-4 rounded-full font-semibold transition-all"
              >
                <Phone className="w-5 h-5" />
                (07) 3708 1422
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ripley-Specific Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
                Bird Proofing for <span style={{ color: theme.primary }}>Ripley's Solar Homes</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Ripley's new estates have one of the highest solar adoption rates in South East Queensland. With so many rooftops fitted with solar panels, pigeons have found plenty of prime nesting spots across Providence, Ecco Ripley, and South Ripley.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Our team services Ripley and surrounding areas including Springfield and Redbank Plains. We remove existing nests, clean up the mess, and install premium mesh protection that keeps birds out permanently.
              </p>
              <Link 
                to="/birds"
                className="inline-flex items-center gap-2 text-[#5BC0DE] font-semibold hover:underline"
              >
                View our full Solar Bird Proofing services <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-[#5BC0DE]/5 rounded-2xl p-8 border-2 border-[#5BC0DE]/20">
              <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#7ABC32]" />
                10-Year Mesh Warranty
              </h3>
              <p className="text-slate-600 mb-4">
                Our professional-grade mesh system comes with a 10-year warranty on materials. We stand behind our work with complete confidence.
              </p>
              <ul className="space-y-2">
                {[
                  "Premium UV-resistant mesh",
                  "Stainless steel clips and fasteners",
                  "No damage to panels or roof",
                  "Maintains panel ventilation"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-600">
                    <Check className="w-5 h-5 text-[#7ABC32] mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Our Bird Control <span style={{ color: theme.primary }}>Services</span>
            </h2>
            <p className="text-lg text-slate-600">Complete bird management solutions for Ripley homes</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-[#5BC0DE]/30 hover:shadow-lg transition-all">
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              What's <span style={{ color: theme.primary }}>Included</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Standard Bird Mesh Installation</h3>
              <ul className="space-y-3">
                {whatsIncluded.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#7ABC32] mt-0.5 shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#7ABC32]/5 rounded-2xl p-8 border-2 border-[#7ABC32]/30">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-xl font-semibold text-slate-900">Premium Gutter Clean + Panel Cleaning</h3>
                <span className="text-xs bg-[#7ABC32] text-white px-2 py-1 rounded-full">Recommended</span>
              </div>
              <p className="text-slate-600 mb-4">
                For properties with extended pigeon nesting (6+ months), we recommend our premium cleaning add-on:
              </p>
              <ul className="space-y-3">
                {premiumCleaningIncludes.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#7ABC32] mt-0.5 shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link 
              to="/birds"
              className="inline-flex items-center gap-2 bg-[#5BC0DE] hover:bg-[#4AA8C4] text-white px-6 py-3 rounded-full font-semibold transition-all"
            >
              Get an Instant Price Quote <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Bird Proofing Across <span className="text-[#5BC0DE]">Ripley & Surrounds</span>
            </h2>
            <p className="text-slate-400">We service all Ripley estates and nearby suburbs</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {ripleyAreas.map((area, i) => (
              <span key={i} className="px-4 py-2 bg-white/10 rounded-full text-white font-medium">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery */}
      <GutterCleanGallery />

      {/* Inline Calculator */}
      <div ref={calculatorRef}>
        <EstimateCalculatorSection />
      </div>

      {/* Back to Main Page CTA */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-600 mb-4">Want to learn more about our bird proofing services?</p>
          <Link 
            to="/birds"
            className="inline-flex items-center gap-2 text-[#5BC0DE] font-semibold hover:underline text-lg"
          >
            Visit our main Solar Bird Proofing page <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// Inline Estimate Calculator Section
function EstimateCalculatorSection() {
  const [panels, setPanels] = useState("");
  const [storeys, setStoreys] = useState("single");
  const [pigeonDuration, setPigeonDuration] = useState("<6m");
  const [addGutterCleaning, setAddGutterCleaning] = useState(false);
  const [addPanelWash, setAddPanelWash] = useState(false);
  const [addPressureWash, setAddPressureWash] = useState(false);
  const [dontKnowPanels, setDontKnowPanels] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Calculate estimate whenever inputs change
  useEffect(() => {
    const panelCount = parseInt(panels) || 0;
    if (panelCount < 1) {
      setEstimate(null);
      return;
    }

    // Tiered pricing (smooth transitions - like tax brackets)
    const tier1Rate = 42;    // panels 1-16
    const tier2Rate = 45.50; // panels 17-26
    const tier3Rate = 49;    // panels 27+
    const nestingAdd = pigeonDuration === ">6m" ? 2 : 0;
    
    let baseTotal;
    let baseRate;
    if (panelCount <= 16) {
      baseTotal = panelCount * (tier1Rate + nestingAdd);
      baseRate = tier1Rate + nestingAdd;
    } else if (panelCount <= 26) {
      baseTotal = (16 * (tier1Rate + nestingAdd)) + ((panelCount - 16) * (tier2Rate + nestingAdd));
      baseRate = tier2Rate + nestingAdd;
    } else {
      baseTotal = (16 * (tier1Rate + nestingAdd)) + (10 * (tier2Rate + nestingAdd)) + ((panelCount - 26) * (tier3Rate + nestingAdd));
      baseRate = tier3Rate + nestingAdd;
    }
    let storeyMultiplier = 1;
    let storeyAddition = 0;
    
    if (storeys === "two") storeyMultiplier = 1.25;
    else if (storeys === "three") {
      storeyMultiplier = 1.4;
      storeyAddition = 400;
    }
    
    // $550 minimum charge (inc GST)
    const calculatedMesh = Math.round((baseTotal * storeyMultiplier) + storeyAddition);
    const meshTotal = Math.max(550, calculatedMesh);
    
    // Cleaning services pricing
    const storeyCleanMult = storeys === "three" ? 1.40 : storeys === "two" ? 1.20 : 1;
    const storeyFlat = storeys === "three" ? 80 : storeys === "two" ? 50 : 0;
    const panelWashLevy = storeys === "three" ? 80 : storeys === "two" ? 50 : 0;
    const gutterCleaningPrice = Math.round((60 + panelCount * 8) * storeyCleanMult + storeyFlat);
    const panelWashPriceCalc = Math.round((25 + panelCount * 5) * storeyCleanMult + storeyFlat + panelWashLevy);
    const pressureWashPriceCalc = Math.round((50 + panelCount * 9) * storeyCleanMult + storeyFlat);

    const selectedCleaningCount = [addGutterCleaning, addPanelWash, addPressureWash].filter(Boolean).length;
    const isCleaningBundle = selectedCleaningCount >= 2;
    const rawCleaningTotal = (addGutterCleaning ? gutterCleaningPrice : 0) + (addPanelWash ? panelWashPriceCalc : 0) + (addPressureWash ? pressureWashPriceCalc : 0);
    const cleaningBundleDiscount = isCleaningBundle ? Math.round(rawCleaningTotal * 0.20) : 0;
    const cleaningTotal = rawCleaningTotal - cleaningBundleDiscount;
    
    const total = meshTotal + cleaningTotal;
    const launchDiscount = Math.round(total * 0.10);
    const discountedTotal = total - launchDiscount;

    setEstimate({ 
      panels: panelCount, storeys, pigeonDuration, rate: baseRate, 
      storeyMultiplier, storeyAddition, meshTotal, gutterCleaningPrice, panelWashPrice: panelWashPriceCalc, pressureWashPrice: pressureWashPriceCalc,
      selectedCleaningCount, isCleaningBundle, cleaningBundleDiscount, cleaningTotal, total,
      launchDiscount, discountedTotal
    });
  }, [panels, storeys, pigeonDuration, addGutterCleaning, addPanelWash, addPressureWash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!estimate) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/solar-estimate`, {
        ...formData,
        panels: estimate?.panels || 0,
        storeys: estimate?.storeys === "two" ? "Two storey" : estimate?.storeys === "three" ? "Three+ storey" : "Single storey",
        pigeonDuration: estimate?.pigeonDuration === ">6m" ? "More than 6 months" : "Less than 6 months",
        ratePerPanel: estimate?.rate,
        meshTotal: estimate?.meshTotal || 0,
        gutterCleaning: addGutterCleaning,
        gutterCleaningPrice: estimate?.gutterCleaningPrice || 0,
        panelWash: addPanelWash,
        panelWashPrice: estimate?.panelWashPrice || 0,
        pressureWash: addPressureWash,
        pressureWashPrice: estimate?.pressureWashPrice || 0,
        cleaningBundleDiscount: estimate?.cleaningBundleDiscount || 0,
        estimatedTotal: estimate?.total || 0,
        dontKnowPanels,
        source: "ripley-landing-page",
        timestamp: new Date().toISOString()
      });
      setSubmitted(true);
      toast.success("Estimate request sent successfully!");
    } catch (error) {
      toast.error("Failed to send estimate. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetCalculator = () => {
    setPanels("");
    setStoreys("single");
    setPigeonDuration("<6m");
    setAddGutterCleaning(false);
    setAddPanelWash(false);
    setAddPressureWash(false);
    setDontKnowPanels(false);
    setEstimate(null);
    setFormData({ name: "", phone: "", email: "", address: "", message: "" });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <section id="calculator" className="py-16 bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <div className="w-16 h-16 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">Estimate Request Sent!</h3>
            <p className="text-lg text-slate-600 mb-6">We'll contact you within 2 hours with a detailed quote.</p>
            <Button onClick={resetCalculator} className="bg-[#5BC0DE] hover:bg-[#4AA8C4] text-white rounded-full px-8 py-4">
              Get Another Estimate
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="calculator" className="py-16 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Logo className="h-16 mx-auto mb-4" variant="dark" />
          <div className="inline-flex items-center gap-2 bg-[#5BC0DE]/10 rounded-full px-4 py-2 mb-4">
            <Calculator className="w-4 h-4 text-[#5BC0DE]" />
            <span className="text-sm font-medium text-[#5BC0DE]">Get Your Price in 60 Seconds</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
            Bird Proofing <span className="text-[#5BC0DE]">Calculator</span>
          </h2>
          <p className="text-lg text-slate-600">Get an instant price estimate for your Ripley property</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="grid lg:grid-cols-5">
            {/* Left Column - Calculator Options */}
            <div className="lg:col-span-3 p-6 md:p-8">
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">Number of Solar Panels</Label>
                  <Input type="number" min="1" value={panels} onChange={(e) => { setPanels(e.target.value); setDontKnowPanels(false); }} placeholder="e.g., 20" className="text-lg py-6" disabled={dontKnowPanels} />
                  <button type="button" onClick={() => { setDontKnowPanels(!dontKnowPanels); if (!dontKnowPanels) setPanels(""); }} className={`mt-2 text-sm font-medium transition-colors ${dontKnowPanels ? 'text-[#7ABC32]' : 'text-[#5BC0DE] hover:text-[#3498aa]'}`} data-testid="dont-know-panels-btn">
                    {dontKnowPanels ? "I know my panel count" : "I don't know my panel count"}
                  </button>
                </div>
                
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">House Height</Label>
                  <div className="flex gap-2">
                    {[
                      { value: "single", label: "1 Storey" },
                      { value: "two", label: "2 Storey" },
                      { value: "three", label: "3+ Storey" }
                    ].map(opt => (
                      <button key={opt.value} type="button" onClick={() => setStoreys(opt.value)}
                        className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
                          storeys === opt.value
                            ? "bg-[#5BC0DE] text-white border-2 border-[#5BC0DE]"
                            : "border-2 border-slate-200 hover:border-slate-300 text-slate-700"
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {storeys === "two" && <p className="text-xs text-slate-500 mt-2 italic">*2 technicians required — harness system & higher ladder access</p>}
                  {storeys === "three" && <p className="text-xs text-slate-500 mt-2 italic">*Boom lift hire, harness system & 2 technicians required</p>}
                </div>
                
                <div>
                  <Label className="text-base font-semibold text-slate-900 mb-3 block">How long have pigeons been nesting?</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ value: "<6m", label: "Less than 6 months" }, { value: ">6m", label: "More than 6 months" }].map((opt) => (
                      <button key={opt.value} type="button" onClick={() => {
                        setPigeonDuration(opt.value);
                        if (opt.value === "<6m") { setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); }
                      }}
                        className={`p-4 rounded-xl border-2 text-center font-medium transition-all ${pigeonDuration === opt.value ? "border-[#5BC0DE] bg-[#5BC0DE]/10 text-[#5BC0DE]" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {pigeonDuration === ">6m" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">Cleaning Services</span>
                      {estimate?.isCleaningBundle && <span className="text-xs bg-[#7ABC32] text-white px-2 py-0.5 rounded-full">Bundle — save 20%!</span>}
                    </div>
                    {estimate?.selectedCleaningCount > 0 && estimate?.selectedCleaningCount < 2 && (
                      <p className="text-xs text-amber-600 font-medium">Select 2+ for a 20% bundle discount</p>
                    )}
                    <div className="space-y-2">
                      {[
                        { key: "gutter", active: addGutterCleaning, toggle: setAddGutterCleaning, label: "Gutter Clean", desc: "Full gutter flush & debris removal", price: estimate?.gutterCleaningPrice, details: ["Remove all debris from gutters", "Flush & clear downpipes", "Dispose of waste material"] },
                        { key: "panel", active: addPanelWash, toggle: setAddPanelWash, label: "Panel Wash", desc: "Surface wash panel tops", price: estimate?.panelWashPrice, details: ["Water-fed broom clean on panel tops", "Remove bird droppings & residue", "Restore panel efficiency"] },
                        { key: "pressure", active: addPressureWash, toggle: setAddPressureWash, label: "Pressure Wash", desc: "Pressure wash & sanitise", price: estimate?.pressureWashPrice, details: ["High-pressure wash around panels", "Sanitise & deodorise affected areas", "Remove staining from roof surface"] },
                      ].map(opt => (
                        <label key={opt.key} className={`block p-3 rounded-xl cursor-pointer border-2 transition-colors ${opt.active ? 'bg-[#7ABC32]/10 border-[#7ABC32]' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`} data-testid={`cleaning-${opt.key}`}>
                          <div className="flex items-center gap-3">
                            <Checkbox checked={opt.active} onCheckedChange={opt.toggle} />
                            <span className="font-semibold text-slate-900 text-sm whitespace-nowrap">{opt.label}</span>
                            <span className="text-xs text-slate-500 hidden sm:inline">— {opt.desc}</span>
                            {opt.price && <span className="text-[#7ABC32] font-bold text-sm ml-auto">${opt.price}</span>}
                          </div>
                          {opt.active && (
                            <ul className="mt-2 ml-8 space-y-0.5">
                              {opt.details.map((d, i) => <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />{d}</li>)}
                            </ul>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Column - Price & Form */}
            <div className="lg:col-span-2 bg-slate-50 p-6 md:p-8 border-t lg:border-t-0 lg:border-l border-slate-200">
              {/* Unified Pricing Box with Logo */}
              <div className={`rounded-2xl overflow-hidden mb-6 ${(estimate || dontKnowPanels) ? 'bg-[#5BC0DE]' : 'bg-slate-300'}`}>
                <div className={`px-5 py-3 flex items-center justify-between ${(estimate || dontKnowPanels) ? 'bg-[#3498aa]' : 'bg-slate-400'}`}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png"
                    alt="Pest Free"
                    className="h-10"
                  />
                  {estimate && <span className="text-amber-400 text-sm font-bold whitespace-nowrap">10% OFF LAUNCH SPECIAL</span>}
                </div>
                
                <div className="p-5 text-white">
                  <p className="text-sm text-white/80 mb-1">Estimated Price</p>
                  {dontKnowPanels ? (
                    <div>
                      <p className="text-2xl font-bold mb-1">No worries!</p>
                      <p className="text-sm text-white/80">Fill in your details and we'll call you with a free quote</p>
                    </div>
                  ) : estimate ? (
                    <>
                      <div className="flex items-baseline gap-3">
                        <span className="text-xl text-white/50 line-through">${estimate.total.toLocaleString()}</span>
                        <span className="text-4xl font-bold">${estimate.discountedTotal.toLocaleString()}</span>
                      </div>
                      <p className="text-amber-400 font-semibold text-sm mt-1">You save ${estimate.launchDiscount.toLocaleString()} (10% off)</p>
                      <p className="text-xs text-yellow-300 font-semibold mt-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>
                      
                      <div className="mt-4 pt-4 border-t border-white/20 text-sm space-y-1">
                        <div className="flex justify-between text-white/80">
                          <span>Solar proofing mesh</span>
                          <span><span className="line-through text-white/40 mr-1">${estimate.meshTotal.toLocaleString()}</span>${Math.round(estimate.meshTotal * 0.9).toLocaleString()}</span>
                        </div>
                        {estimate.storeyMultiplier > 1 && <p className="text-xs text-white/60 pl-2">incl. height loading</p>}
                        {estimate.storeyAddition > 0 && <p className="text-xs text-white/60 pl-2">incl. ${estimate.storeyAddition} boom lift</p>}
                        {estimate.cleaningTotal > 0 && (
                          <div className="flex justify-between text-white/80">
                            <span>+ Cleaning services</span>
                            <span>${estimate.cleaningTotal}</span>
                          </div>
                        )}
                        {estimate.isCleaningBundle && <p className="text-xs text-[#7ABC32] pl-2">incl. 20% bundle discount</p>}
                        <div className="text-white/50 text-[10px] pt-1 border-t border-white/10">Prices include GST</div>
                      </div>
                    </>
                  ) : (
                    <div className="text-4xl font-bold">$—</div>
                  )}
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
                <p className="font-semibold text-slate-900 mb-2">What's Included:</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> Removal of all nests & debris</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> Professional-grade mesh</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> 10-year warranty on mesh</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#7ABC32]" /> Before & after photos/videos</li>
                  {(addGutterCleaning || addPanelWash || addPressureWash) && (
                    <>
                      {addGutterCleaning && <li className="flex items-center gap-2 text-[#7ABC32] font-medium"><Check className="w-4 h-4" /> Full gutter flush & debris removal</li>}
                      {addPanelWash && <li className="flex items-center gap-2 text-[#7ABC32] font-medium"><Check className="w-4 h-4" /> Solar panel surface wash</li>}
                      {addPressureWash && <li className="flex items-center gap-2 text-[#7ABC32] font-medium"><Check className="w-4 h-4" /> Pressure wash & sanitise</li>}
                    </>
                  )}
                </ul>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1 block">Name *</Label>
                    <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Your name" className="bg-white" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1 block">Phone *</Label>
                    <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="0400 000 000" className="bg-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1 block">Email *</Label>
                  <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" className="bg-white" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1 block">Address *</Label>
                  <Textarea required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="e.g., 123 Example Street, Ripley QLD 4306" rows={2} className="bg-white" />
                </div>
                
                <p className="text-xs text-slate-500 italic">Final pricing subject to inspection and property conditions.</p>
                
                <div className="flex gap-3">
                  <Button type="submit" disabled={(!estimate && !dontKnowPanels) || submitting} className="flex-1 bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 rounded-xl font-semibold">
                    {submitting ? "Sending..." : dontKnowPanels ? "Request a Callback" : "Get My Quote Now"} <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                  <Button type="button" onClick={resetCalculator} variant="outline" className="px-4 py-6 rounded-xl">
                    <RotateCcw className="w-5 h-5" />
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        {/* Quick Callback Option */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 mb-3">Prefer to talk? We'll call you back within 30 minutes.</p>
          <a 
            href="tel:0737081422"
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-full font-medium transition-colors"
          >
            <Phone className="w-5 h-5" />
            Call (07) 3708 1422
          </a>
        </div>
      </div>
    </section>
  );
}
